import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { AlertTriangle, RefreshCw, UserMinus } from 'lucide-react';
import { type AccountDeletionWebRequestItem } from '../api/adminOps';
import { useAuth } from '../components/AuthProvider';
import CancelDeletionAction from '../components/CancelDeletionAction';
import { DetailDrawer, QueueTable, StatusChip, type QueueColumn } from '../components/ui';
import { useAccountDeletionRequests, useUserDeletion } from '../hooks/useAdminOps';
import {
  cancelGate,
  emptyHint,
  filterLabel,
  queueParams,
  requestDisplay,
  STATUS_FILTERS,
  statusChip,
  type StatusFilter,
} from './accountDeletionRequests.logic';
import { deletionLookupState, enforcementHref } from './userDeletion.logic';

function chip(status: string) {
  const config = statusChip(status);
  return <StatusChip label={config.label} tone={config.tone} />;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-gray-text">{label}</dt>
      <dd className="text-white break-words">{children}</dd>
    </div>
  );
}

export default function AccountDeletionRequests() {
  const { hasRole } = useAuth();
  const canWrite = hasRole('admin', 'admin-operator');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const queue = useAccountDeletionRequests(queueParams(statusFilter));
  const selected = queue.rows.find(row => row.id === selectedId) ?? null;

  // Read only for a CONFIRMED row: no other status can be cancelled, and each
  // read of an account's deletion state is written to the audit ledger.
  const deletion = useUserDeletion(selected?.status === 'CONFIRMED' ? selected.user_id : null);
  const lookup = deletionLookupState({
    isLoading: deletion.isLoading,
    isError: deletion.isError,
    hasData: deletion.data !== undefined,
  });
  const gate =
    selected === null ? null : cancelGate(selected, lookup, deletion.data?.data ?? null, canWrite);

  const now = new Date();
  const display = selected === null ? null : requestDisplay(selected, now);

  const columns: QueueColumn<AccountDeletionWebRequestItem>[] = [
    {
      key: 'created',
      header: 'Submitted',
      render: row => (
        <span className="text-gray-text whitespace-nowrap">
          {formatDistanceToNow(new Date(row.created_at), { addSuffix: true })}
        </span>
      ),
    },
    { key: 'status', header: 'Status', render: row => chip(row.status) },
    {
      key: 'name',
      header: 'Name given',
      // React escapes text children, which is the whole defence here: the
      // name and message are typed by an anonymous visitor to a public form.
      render: row => <span className="break-words">{requestDisplay(row, now).requesterName}</span>,
    },
    {
      key: 'user',
      header: 'User id',
      render: row => (
        <span className="text-gray-text font-mono text-xs break-all">{row.user_id}</span>
      ),
    },
    {
      key: 'waiting',
      header: 'Waiting period ends',
      render: row => (
        <span className="text-gray-text whitespace-nowrap">
          {requestDisplay(row, now).waitingPeriodEnds}
        </span>
      ),
    },
  ];

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
            <UserMinus className="w-6 h-6 text-primary" /> Deletion Requests
          </h1>
          <p className="text-gray-text text-sm mt-1">
            Account deletions asked for from the website. A request does nothing until the requester
            confirms it from the emailed link; the account then enters its waiting period. To delete
            an account for someone who wrote in by email, use the Enforcement page.
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary flex items-center space-x-2"
          onClick={() => queue.refetch()}
          disabled={queue.isFetching}
        >
          <RefreshCw className={`w-4 h-4 ${queue.isFetching ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      <div
        className="glass-card p-4 flex items-center gap-2 flex-wrap"
        role="group"
        aria-label="Filter by status"
      >
        <span className="text-sm text-gray-text mr-2">Status:</span>
        {(['all', ...STATUS_FILTERS] as StatusFilter[]).map(value => (
          <button
            type="button"
            key={value}
            aria-pressed={statusFilter === value}
            onClick={() => {
              setStatusFilter(value);
              setSelectedId(null);
            }}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              statusFilter === value
                ? 'bg-primary/20 text-primary'
                : 'bg-gray-500/10 text-gray-text hover:text-white'
            }`}
          >
            {filterLabel(value)}
          </button>
        ))}
      </div>

      {queue.isError ? (
        <div
          className="glass-card p-6 border border-red-500/30 flex items-start gap-3"
          role="alert"
        >
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-white font-medium mb-1">Failed to load deletion requests</p>
            <p className="text-sm text-gray-text mb-3">
              {queue.error instanceof Error ? queue.error.message : 'Unknown error'}
            </p>
            <button type="button" onClick={() => queue.refetch()} className="btn-secondary">
              Retry
            </button>
          </div>
        </div>
      ) : (
        <QueueTable
          columns={columns}
          rows={queue.rows}
          rowKey={row => row.id}
          isLoading={queue.isPending}
          onRowClick={row => setSelectedId(row.id)}
          emptyTitle="No deletion requests"
          emptyHint={emptyHint(statusFilter)}
          hasNextPage={queue.hasNextPage}
          isFetchingNextPage={queue.isFetchingNextPage}
          onLoadMore={() => queue.fetchNextPage()}
        />
      )}

      <DetailDrawer
        open={selected !== null}
        title="Deletion request"
        onClose={() => setSelectedId(null)}
        footer={
          selected &&
          gate && (
            <div className="space-y-3">
              {gate.canCancel && (
                <CancelDeletionAction
                  // Keyed on the row so a note typed for one request can never
                  // be submitted against the next one opened.
                  key={selected.id}
                  userId={selected.user_id}
                  idPrefix="deletion-request"
                  disabled={deletion.isFetching}
                  onCancelled={() => setSelectedId(null)}
                />
              )}
              <p className="text-xs text-gray-text" aria-live="polite">
                {gate.note}
              </p>
              {selected.status === 'CONFIRMED' && lookup === 'error' && (
                <button
                  type="button"
                  className="btn-secondary w-full"
                  onClick={() => deletion.refetch()}
                >
                  Retry
                </button>
              )}
              <Link
                to={enforcementHref(selected.user_id)}
                className="btn-ghost w-full block text-center"
              >
                Open this account’s enforcement page
              </Link>
            </div>
          )
        }
      >
        {selected && display && (
          <div className="space-y-4 text-sm">
            <div className="flex items-center gap-2 flex-wrap">{chip(selected.status)}</div>
            <p className="text-xs text-gray-text">{statusChip(selected.status).hint}</p>

            <dl className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Field label="User id">
                  <span className="font-mono text-xs break-all">{selected.user_id}</span>
                </Field>
              </div>
              <Field label="Submitted">{display.submitted}</Field>
              <Field label="Source">{display.source}</Field>
              <Field label="Confirmed">{display.confirmed}</Field>
              <Field label="Confirmation link expires">{display.linkExpires}</Field>
              <div className="col-span-2">
                <Field label="Waiting period ends">{display.waitingPeriodEnds}</Field>
                <p className="text-xs text-gray-text mt-1">{display.waitingPeriodNote}</p>
              </div>
              {selected.deletion_request_id && (
                <div className="col-span-2">
                  <Field label="Deletion request id">
                    <span className="font-mono text-xs break-all">
                      {selected.deletion_request_id}
                    </span>
                  </Field>
                </div>
              )}
            </dl>

            <div className="border-t border-gray-border pt-4 space-y-3">
              <p className="text-xs text-gray-text">
                Typed by the requester on a public form. Shown exactly as submitted — treat it as
                unverified.
              </p>
              <div>
                <p className="text-xs text-gray-text">Name given</p>
                <p className={`break-words ${display.hasName ? 'text-white' : 'text-gray-text'}`}>
                  {display.requesterName}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-text">Message</p>
                <p
                  className={`whitespace-pre-wrap break-words ${
                    display.hasMessage ? 'text-white' : 'text-gray-text'
                  }`}
                >
                  {display.message}
                </p>
              </div>
            </div>
          </div>
        )}
      </DetailDrawer>
    </div>
  );
}
